import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Building, Plus, Trash2, Loader2, Edit2, Users, 
  ChevronRight, Building2, UserPlus, Search, Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

export const OrganizationsPage = () => {
  const { api, user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [orgForm, setOrgForm] = useState({ name: '', code: '', notes: '' });
  
  // Supervisore dialog
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [selectedOrgForSupervisor, setSelectedOrgForSupervisor] = useState(null);
  const [supervisorForm, setSupervisorForm] = useState({ username: '', password: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Supervisors list
  const [supervisors, setSupervisors] = useState([]);

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const res = await api.get('/organizations');
      setOrganizations(res.data);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Errore nel caricamento delle organizzazioni');
    }
  }, [api]);

  // Fetch supervisors
  const fetchSupervisors = useCallback(async () => {
    try {
      const res = await api.get('/users');
      const sups = res.data.filter(u => u.role === 'supervisore');
      setSupervisors(sups);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    }
  }, [api]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOrganizations(), fetchSupervisors()]);
      setLoading(false);
    };
    loadData();
  }, [fetchOrganizations, fetchSupervisors]);

  // Organization CRUD
  const handleOrgSubmit = async (e) => {
    e.preventDefault();
    if (!orgForm.name.trim() || !orgForm.code.trim()) {
      toast.error('Inserisci nome e codice');
      return;
    }

    try {
      if (editingOrg) {
        await api.put(`/organizations/${editingOrg.id}`, {
          name: orgForm.name,
          notes: orgForm.notes
        });
        toast.success('Organizzazione aggiornata');
      } else {
        await api.post('/organizations', orgForm);
        toast.success('Organizzazione creata');
      }
      
      setOrgDialogOpen(false);
      setEditingOrg(null);
      setOrgForm({ name: '', code: '', notes: '' });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`Eliminare "${org.name}"? Questa azione è irreversibile.`)) return;
    
    try {
      await api.delete(`/organizations/${org.id}`);
      toast.success('Organizzazione eliminata');
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'eliminazione');
    }
  };

  const openEditOrg = (org) => {
    setEditingOrg(org);
    setOrgForm({
      name: org.name,
      code: org.code,
      notes: org.notes || ''
    });
    setOrgDialogOpen(true);
  };

  // Supervisore CRUD
  const handleCreateSupervisor = async (e) => {
    e.preventDefault();
    if (!supervisorForm.username || !supervisorForm.password) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    setCreatingUser(true);
    try {
      await api.post('/auth/register', {
        username: supervisorForm.username,
        password: supervisorForm.password,
        role: 'supervisore',
        organization_id: selectedOrgForSupervisor.id
      });
      toast.success('Supervisore creato con successo');
      setSupervisorDialogOpen(false);
      setSupervisorForm({ username: '', password: '' });
      setSelectedOrgForSupervisor(null);
      fetchSupervisors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setCreatingUser(false);
    }
  };

  const openSupervisorDialog = (org) => {
    setSelectedOrgForSupervisor(org);
    setSupervisorDialogOpen(true);
  };

  // Get supervisors for an organization
  const getOrgSupervisors = (orgId) => {
    return supervisors.filter(s => s.organization_id === orgId);
  };

  // Filter organizations
  const filteredOrganizations = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="organizations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
            <Briefcase className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Organizzazioni</h1>
            <p className="text-slate-500 text-sm">Gestisci aziende e i loro supervisori (CEO)</p>
          </div>
        </div>
        
        {isSuperAdmin && (
          <Button
            onClick={() => {
              setEditingOrg(null);
              setOrgForm({ name: '', code: '', notes: '' });
              setOrgDialogOpen(true);
            }}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="add-org-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Organizzazione
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cerca organizzazione..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-org-input"
        />
      </div>

      {/* Organizations List */}
      {filteredOrganizations.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Building className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna organizzazione trovata</p>
            {isSuperAdmin && (
              <p className="text-slate-400 text-sm mt-1">
                Clicca "Nuova Organizzazione" per crearne una
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredOrganizations.map(org => {
            const orgSupervisors = getOrgSupervisors(org.id);
            
            return (
              <Card 
                key={org.id} 
                className="hover:shadow-md transition-shadow border-purple-100"
                data-testid={`org-card-${org.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                        <Building className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{org.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">
                          {org.code}
                        </Badge>
                      </div>
                    </div>
                    {isSuperAdmin && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditOrg(org)}
                          data-testid={`edit-org-${org.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteOrg(org)}
                          disabled={org.structures_count > 0}
                          title={org.structures_count > 0 ? 'Rimuovi prima le strutture' : 'Elimina'}
                          data-testid={`delete-org-${org.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {org.notes && (
                    <p className="text-sm text-slate-500">{org.notes}</p>
                  )}
                  
                  {/* Stats */}
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="w-4 h-4 text-purple-500" />
                      <span>{org.structures_count} strutture</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-600">
                      <Users className="w-4 h-4 text-purple-500" />
                      <span>{orgSupervisors.length} supervisori</span>
                    </div>
                  </div>
                  
                  {/* Supervisors */}
                  {orgSupervisors.length > 0 && (
                    <div className="pt-3 border-t">
                      <div className="text-xs font-medium text-slate-500 mb-2">SUPERVISORI (CEO)</div>
                      <div className="space-y-2">
                        {orgSupervisors.map(sup => (
                          <div 
                            key={sup.id}
                            className="flex items-center gap-2 text-sm bg-purple-50 px-2 py-1 rounded"
                          >
                            <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center text-xs font-bold text-purple-700">
                              {sup.username.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium">{sup.username}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Add Supervisor Button */}
                  {isSuperAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
                      onClick={() => openSupervisorDialog(org)}
                      data-testid={`add-supervisor-${org.id}`}
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Aggiungi Supervisore
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Organization Dialog */}
      <Dialog open={orgDialogOpen} onOpenChange={setOrgDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingOrg ? 'Modifica Organizzazione' : 'Nuova Organizzazione'}
            </DialogTitle>
            <DialogDescription>
              Le organizzazioni raggruppano più strutture sotto un unico CEO
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOrgSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Azienda *</Label>
              <Input
                value={orgForm.name}
                onChange={e => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Es. Gruppo Sanitario Rossi"
                data-testid="org-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Codice Univoco *</Label>
              <Input
                value={orgForm.code}
                onChange={e => setOrgForm(prev => ({ ...prev, code: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
                placeholder="Es. gruppo-rossi"
                disabled={!!editingOrg}
                data-testid="org-code-input"
              />
              <p className="text-xs text-slate-500">Solo lettere minuscole, numeri e trattini</p>
            </div>
            
            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                value={orgForm.notes}
                onChange={e => setOrgForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note aggiuntive..."
              />
            </div>
            
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700">
              {editingOrg ? 'Salva Modifiche' : 'Crea Organizzazione'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supervisor Dialog */}
      <Dialog open={supervisorDialogOpen} onOpenChange={setSupervisorDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuovo Supervisore (CEO)</DialogTitle>
            <DialogDescription>
              {selectedOrgForSupervisor && (
                <>Crea un supervisore per <strong>{selectedOrgForSupervisor.name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSupervisor} className="space-y-4">
            <div className="space-y-2">
              <Label>Username *</Label>
              <Input
                value={supervisorForm.username}
                onChange={e => setSupervisorForm(prev => ({ ...prev, username: e.target.value }))}
                placeholder="Es. ceo.rossi"
                data-testid="supervisor-username-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={supervisorForm.password}
                onChange={e => setSupervisorForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Password sicura"
                data-testid="supervisor-password-input"
              />
            </div>
            
            <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-700">
              <strong>Il Supervisore potrà:</strong>
              <ul className="mt-1 list-disc list-inside text-xs">
                <li>Vedere tutte le strutture della sua organizzazione</li>
                <li>Modificare le impostazioni delle strutture</li>
                <li>Visualizzare statistiche e report aggregati</li>
              </ul>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={creatingUser}
            >
              {creatingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Crea Supervisore
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
